// Маршруты полей пункта и протокола без файла (014: contracts/api.md, «Маршруты»; research,
// Р1, Р4–Р6). Поиск записей справочника для поповера «Ответственные» — по праву протокола,
// а не справочника: у протокола без файла упоминаний нет (Р1). Ведущий — роль `lead` с одной
// областью документа; выдаёт создатель протокола (решение владельца 23.10.2026).
import { z } from '../kernel/zod.ts';
import { IdSchema } from '../kernel/common.ts';
import { ExternalTypeSchema, OrganizationSideSchema } from '../directory/record.ts';
import { partyFields, withoutControlChars } from './item.ts';

/** Название протокола без файла (FR-032). */
export const PROTOCOL_TITLE_MAX = 200;
/** Строка поиска кандидатов в ведущие и записей справочника. */
export const LOOKUP_QUERY_MIN = 2;
export const LOOKUP_QUERY_MAX = 100;
export const LEAD_CANDIDATES_LIMIT = 20;
export const PEOPLE_SEARCH_LIMIT = 10;

const TitleSchema = z
  .string()
  .max(PROTOCOL_TITLE_MAX)
  .refine((title) => title.trim().length > 0, { message: 'Введите название' })
  .refine((title) => withoutControlChars(title) === title && !/[\n\r\t]/.test(title), {
    message: 'В названии — управляющий знак или перевод строки',
  });

/** «Протокол в системе» (FR-032): название и ведущий по желанию. */
export const CreateProtocolBodySchema = z.strictObject({
  title: TitleSchema,
  leadUserId: IdSchema.optional(),
  folderId: IdSchema.nullable().optional(),
});
export type CreateProtocolBody = z.infer<typeof CreateProtocolBodySchema>;

export const CreatedProtocolSchema = z.object({
  documentId: IdSchema,
  /** П-N. */
  number: z.number().int().min(1),
});
export type CreatedProtocol = z.infer<typeof CreatedProtocolSchema>;

/** Ведущий протокола: пользователь с ролью «Ведущий» и областью документа (FR-033). */
export const ProtocolLeadSchema = z.object({ userId: IdSchema, name: z.string() });
export type ProtocolLead = z.infer<typeof ProtocolLeadSchema>;

/**
 * Смена ведущего (FR-033). Последняя запись побеждает. Снять ведущего без замены — нет действия
 * в интерфейсе (по запросу владельца, матрица операций), поэтому и в API его нет (принцип XI).
 */
export const SetLeadBodySchema = z.strictObject({ userId: IdSchema });

export const SetLeadResponseSchema = z.object({ lead: ProtocolLeadSchema.nullable() });

const LookupQuerySchema = z.object({
  q: z.string().trim().min(LOOKUP_QUERY_MIN).max(LOOKUP_QUERY_MAX),
});

/** Кандидаты в ведущие: действующие пользователи, только номер и имя (security CHK017). */
export const LeadCandidatesQuerySchema = LookupQuerySchema;
export const LeadCandidatesResponseSchema = z.object({
  items: z.array(z.object({ id: IdSchema, name: z.string() })).max(LEAD_CANDIDATES_LIMIT),
});

/**
 * Поиск записей справочника для поповеров «Ответственные» и «Контрагенты» (research, Р1; 027:
 * FR-002, FR-004): только записи вида `kind` (по умолчанию — люди). У человека — организация
 * и должность основного места работы, у организации — своя или внешняя и тип.
 */
/** Организаций пункта в поиске поповера (028: FR-007). */
export const PEOPLE_SEARCH_ORGS_MAX = 20;

/**
 * 028 (FR-007; research, Р4): `orgIds` — организации «Контрагента» пункта. Без текста (`q` пуст)
 * — сотрудники этих организаций по основному месту работы; с текстом — обычный поиск, сотрудники
 * этих организаций первыми. Без `orgIds` текст обязателен (от двух знаков), как раньше.
 */
export const PeopleSearchQuerySchema = z
  .object({
    q: z.string().trim().max(LOOKUP_QUERY_MAX).default(''),
    kind: z.enum(['person', 'organization']).default('person'),
    orgIds: z
      .preprocess(
        (value) => (typeof value === 'string' ? value.split(',').filter(Boolean) : value),
        z.array(IdSchema).max(PEOPLE_SEARCH_ORGS_MAX),
      )
      .optional(),
  })
  .refine((query) => query.q.length >= LOOKUP_QUERY_MIN || (query.orgIds?.length ?? 0) > 0, {
    message: `Введите не меньше ${LOOKUP_QUERY_MIN} знаков`,
    path: ['q'],
  });
export const PeopleSearchResponseSchema = z.object({
  items: z
    .array(
      z.object({
        id: IdSchema,
        kind: z.enum(['person', 'organization']),
        name: z.string(),
        organization: z.string().nullable(),
        /**
         * 028 (FR-008): организация основного места работы человека — номер и своя или внешняя:
         * поповер предлагает «Добавить «…» в Контрагент» для внешней, которой нет у пункта.
         */
        organizationId: IdSchema.nullable().optional(),
        organizationSide: OrganizationSideSchema.nullable().optional(),
        position: z.string().nullable().optional(),
        side: OrganizationSideSchema.nullable().optional(),
        externalType: ExternalTypeSchema.nullable().optional(),
        state: z.enum(['confirmed', 'draft']),
      }),
    )
    .max(PEOPLE_SEARCH_LIMIT),
});
export type PeopleSearchResponse = z.infer<typeof PeopleSearchResponseSchema>;

/**
 * Ленивый поиск вариантов фильтра: доступен всем читателям рабочей недели, без карточек. 027: поле
 * фильтра — «Ответственные ▾» (люди) или «Контрагенты ▾» (организации).
 */
export const AssigneeOptionsQuerySchema = LookupQuerySchema.extend({
  field: z.enum(partyFields).default('assignees'),
});
export const AssigneeOptionsResponseSchema = z.object({
  items: z
    .array(
      z.strictObject({
        id: IdSchema,
        kind: z.enum(['person', 'organization']),
        name: z.string(),
      }),
    )
    .max(PEOPLE_SEARCH_LIMIT),
});
export type AssigneeOptionsResponse = z.infer<typeof AssigneeOptionsResponseSchema>;
